import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CrossHeaderActions } from '@triedge/ai-assistant-ui';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconLayoutDashboard,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
  IconLogout,
  IconSettings,
  IconUsers,
  IconWallet,
} from '@tabler/icons-react';
import styles from './AppShell.module.css';
import {
  STAFF_TEMP_NAV_DISCLOSURE_STORAGE_KEY,
  useTempNavDisclosure,
} from './tempNavDisclosure';
import {
  areCognitoProfilesEquivalent,
  canUseShellThemeToggle,
  resolveAppShellPersonaLabel,
  resolveCognitoProfile,
  type CognitoProfile,
} from '../../utils/cognito-profile';
import { useNotificationsCenter } from '../../notifications/useNotificationsCenter';
import {
  CrossHeaderNotificationsMenu,
  mapCrossHeaderNotificationsToMenuItems,
  type NotificationMenuItem,
} from './CrossHeaderNotificationsMenu';
import { runBlockingLogout } from './logoutBlocking';
import {
  ASSISTANT_OVERLAY_REQUEST_CLOSE_EVENT,
  emitAssistantShellState,
} from '../../ai/assistantOverlayEvents';
import { parseAssistantPanelV2ProofStateFromSearch } from '../ai/assistantProofPresets';
import { useYstnjNightMode } from '../../theme/useYstnjNightMode';
import { ShellThemeToggle } from './ShellThemeToggle';
import { runSafeNotificationNavigation } from './notificationNavigationSafety';

export type SettingsNavItem = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

export type SettingsTab = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

export const DEFAULT_MAIN_NAV: SettingsNavItem[] = [
  { id: 'admissions', label: 'Admissions', icon: IconLayoutDashboard },
  { id: 'financial-aid', label: 'Financial Aid', icon: IconWallet },
];

export const DEFAULT_SETTINGS_ITEM: SettingsNavItem = {
  id: 'settings',
  label: 'Settings',
  icon: IconSettings,
};

export const DEFAULT_SETTINGS_TABS: SettingsTab[] = [
  { id: 'general', label: 'General', icon: IconSettings },
  { id: 'users', label: 'Users', icon: IconUsers },
  { id: 'admissions', label: 'Admissions', icon: IconLayoutDashboard },
  { id: 'financial-aid', label: 'Financial Aid', icon: IconCircleCheck },
];

export type AppShellProps = {
  title?: string;
  subtitle?: string;
  showSubtitle?: boolean;
  showHeader?: boolean;
  showHeaderTitle?: boolean;
  showAssistant?: boolean;
  navOpen?: boolean;
  assistantOpen?: boolean;
  assistantResizable?: boolean;
  assistantWidthMode?: 'compact' | 'expanded' | 'half' | 'full';
  lockAssistantOpen?: boolean;
  showHeaderActions?: boolean;
  navItems?: SettingsNavItem[];
  secondaryNavItems?: SettingsNavItem[];
  secondaryNavLabel?: string;
  settingsItem?: SettingsNavItem;
  activeNavId?: string;
  tabs?: SettingsTab[];
  activeCategory?: string;
  showTabs?: boolean;
  tabsPlacement?: 'below-header' | 'before-title';
  tabsOrientation?: 'horizontal' | 'vertical';
  contentVariant?: 'panel' | 'bleed';
  contentInsetMode?: 'default' | 'none';
  mainLayoutMode?: 'default' | 'fill-height';
  mainPaddingProfile?: 'default' | 'school-financial-aid-qa0219';
  forceSidebarRailOnCompact?: boolean;
  assistantDefaultMode?: 'standard' | 'closed-mobile-school';
  headerClassName?: string;
  forceHeaderMarginBottomZero?: boolean;
  forceMainMarginRightZero?: boolean;
  status?: React.ReactNode;
  assistant?: React.ReactNode;
  children?: React.ReactNode;
  /** Full-width banner rendered between the header and main content. Shell-owned slot for admin chrome banners (impersonation, view-as). */
  adminChromeBanner?: React.ReactNode;
  /** Node rendered inside the header actions region, before CrossHeaderActions. Shell-owned slot for admin context chip and context switcher. */
  headerAdminChrome?: React.ReactNode;
  /** Node rendered next to the user avatar inside the sidebar user card. */
  userCardAction?: React.ReactNode;
  onNavChange?: (id: string) => void;
  onCategoryChange?: (id: string) => void;
  onNavOpenChange?: (open: boolean) => void;
  onAssistantOpenChange?: (open: boolean) => void;
  onAssistantWidthModeChange?: (mode: 'compact' | 'expanded' | 'half' | 'full') => void;
  onNotificationSelect?: (notificationId: string, route: string | null) => void | Promise<void>;
  onLogout?: () => void | Promise<void>;
  notificationMenuItems?: NotificationMenuItem[];
  notificationUnreadCount?: number;
  notificationIsLoading?: boolean;
  notificationError?: string | null;
};

const normalizeCategory = (value?: string | null): string =>
  String(value ?? '').toLowerCase().replace(/_/g, '-');

const getInitials = (value?: string | null): string => {
  if (!value) return 'US';
  const cleaned = value
    .replace(/[^A-Za-z0-9\s]/g, ' ')
    .split(' ')
    .filter(Boolean);
  if (!cleaned.length) return 'US';
  if (cleaned.length === 1) return cleaned[0].slice(0, 2).toUpperCase();
  return `${cleaned[0][0]}${cleaned[cleaned.length - 1][0]}`.toUpperCase();
};

const SCHOOL_PERSONA_NOTIFICATION_STREAM_KEY = 'school-persona-feed';

const isAppShellMobileViewport = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(max-width: 1024px)').matches;
};

const readAssistantPanelV2ParityWidthMode = (): {
  active: boolean;
  widthMode: AppShellProps['assistantWidthMode'] | null;
} => {
  if (typeof window === 'undefined') {
    return { active: false, widthMode: null };
  }
  const proofState = parseAssistantPanelV2ProofStateFromSearch(window.location.search);
  return {
    active: Boolean(proofState),
    widthMode: proofState?.widthMode ?? null,
  };
};

const resolveStaffNotificationRoute = (item: NotificationMenuItem): string | null => {
  const subject = String(item.text ?? '').toLowerCase();
  const applicationId = String(item.applicationId ?? '').trim();
  const encodedNotificationId = encodeURIComponent(item.id);

  if (applicationId) {
    const encodedApplicationId = encodeURIComponent(applicationId);
    if (
      subject.includes('financial aid')
      || subject.includes('scholarship')
      || subject.includes('appeal')
      || subject.includes('award')
    ) {
      return `/school/financialaid/staff/${encodedApplicationId}/family-profile`;
    }
    return `/school/admissions/children/${encodedApplicationId}`;
  }

  if (
    subject.includes('financial aid')
    || subject.includes('scholarship')
    || subject.includes('appeal')
    || subject.includes('award')
  ) {
    return '/school/financialaid/dashboard';
  }

  return `/school/admissions/dashboard?notificationId=${encodedNotificationId}`;
};

export const AppShell: React.FC<AppShellProps> = ({
  title = 'Settings',
  subtitle,
  showSubtitle,
  showHeader = true,
  showHeaderTitle,
  showAssistant = true,
  navOpen = false,
  assistantOpen = true,
  assistantResizable = false,
  assistantWidthMode = 'compact',
  lockAssistantOpen = false,
  showHeaderActions = false,
  navItems = DEFAULT_MAIN_NAV,
  secondaryNavItems = [],
  secondaryNavLabel = 'Temp',
  settingsItem,
  activeNavId = navItems[0]?.id ?? settingsItem?.id ?? '',
  tabs = DEFAULT_SETTINGS_TABS,
  activeCategory,
  showTabs = true,
  tabsPlacement = 'before-title',
  tabsOrientation = 'vertical',
  contentVariant = 'panel',
  contentInsetMode = 'default',
  mainLayoutMode = 'default',
  mainPaddingProfile = 'default',
  forceSidebarRailOnCompact = false,
  assistantDefaultMode = 'standard',
  headerClassName = '',
  forceHeaderMarginBottomZero = false,
  forceMainMarginRightZero = false,
  status,
  assistant,
  children,
  adminChromeBanner,
  headerAdminChrome,
  userCardAction,
  onNavChange,
  onCategoryChange,
  onNavOpenChange,
  onAssistantOpenChange,
  onAssistantWidthModeChange,
  onNotificationSelect,
  onLogout,
  notificationMenuItems,
  notificationUnreadCount,
  notificationIsLoading,
  notificationError,
}) => {
  const [navPinnedOpen, setNavPinnedOpen] = useState(navOpen);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarFocusWithin, setSidebarFocusWithin] = useState(false);
  const [sidebarPointerDown, setSidebarPointerDown] = useState(false);
  const startsWithClosedMobileAssistantDefault =
    assistantDefaultMode === 'closed-mobile-school' && isAppShellMobileViewport();
  const mobileAssistantDefaultAppliedRef = useRef(startsWithClosedMobileAssistantDefault);
  const [assistantExpanded, setAssistantExpanded] = useState(() => (
    showAssistant && !startsWithClosedMobileAssistantDefault ? assistantOpen : false
  ));
  const [assistantPanelV2Parity, setAssistantPanelV2Parity] = useState(() => readAssistantPanelV2ParityWidthMode());
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => isAppShellMobileViewport());
  const logoutStateRef = useRef({ inFlight: false });
  const notificationSelectionPendingRef = useRef(false);
  const headerButtonsRef = useRef<HTMLDivElement | null>(null);
  const [userProfile, setUserProfile] = useState<CognitoProfile | null>(() => resolveCognitoProfile());
  const { enabled: nightModeEnabled, toggle: toggleNightMode } = useYstnjNightMode({ profile: userProfile });

  useEffect(() => setNavPinnedOpen(navOpen), [navOpen]);
  useEffect(() => {
    if (!showAssistant) {
      setAssistantExpanded(false);
      return;
    }
    const shouldApplyClosedMobileAssistantDefault =
      assistantDefaultMode === 'closed-mobile-school' && isMobileViewport;
    if (
      shouldApplyClosedMobileAssistantDefault
      && !mobileAssistantDefaultAppliedRef.current
    ) {
      mobileAssistantDefaultAppliedRef.current = true;
      setAssistantExpanded(false);
      return;
    }
    setAssistantExpanded(assistantOpen);
  }, [assistantDefaultMode, assistantOpen, isMobileViewport, showAssistant]);
  useEffect(() => {
    if (assistantDefaultMode === 'closed-mobile-school' && isMobileViewport) {
      return;
    }
    mobileAssistantDefaultAppliedRef.current = false;
  }, [assistantDefaultMode, isMobileViewport]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const syncAssistantPanelV2Parity = () => {
      setAssistantPanelV2Parity(readAssistantPanelV2ParityWidthMode());
    };
    syncAssistantPanelV2Parity();
    window.addEventListener('popstate', syncAssistantPanelV2Parity);
    return () => {
      window.removeEventListener('popstate', syncAssistantPanelV2Parity);
    };
  }, []);
  useEffect(() => {
    if (!sidebarPointerDown || typeof window === 'undefined') {
      return undefined;
    }
    const clearPointerDown = () => setSidebarPointerDown(false);
    window.addEventListener('pointerup', clearPointerDown);
    window.addEventListener('pointercancel', clearPointerDown);
    window.addEventListener('blur', clearPointerDown);
    return () => {
      window.removeEventListener('pointerup', clearPointerDown);
      window.removeEventListener('pointercancel', clearPointerDown);
      window.removeEventListener('blur', clearPointerDown);
    };
  }, [sidebarPointerDown]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const media = window.matchMedia('(max-width: 1024px)');
    const syncIsMobileViewport = () => setIsMobileViewport(media.matches);
    syncIsMobileViewport();
    media.addEventListener('change', syncIsMobileViewport);
    return () => media.removeEventListener('change', syncIsMobileViewport);
  }, []);

  const normalizedCategory = normalizeCategory(activeCategory) || tabs[0]?.id || 'general';
  const activeTab = useMemo(
    () => tabs.find((tab) => normalizeCategory(tab.id) === normalizedCategory) || tabs[0],
    [normalizedCategory, tabs]
  );
  const mainPaddingProfileClass = mainPaddingProfile === 'school-financial-aid-qa0219'
    ? styles.mainInsetFinancialAidQa0219
    : '';
  const mainLayoutClass = mainLayoutMode === 'fill-height'
    ? styles.mainFillHeight
    : '';
  const contentInsetClass = contentInsetMode === 'none'
    ? styles.contentNoInset
    : '';
  const navExpanded = isMobileViewport
    ? navPinnedOpen
    : (navPinnedOpen || sidebarHovered || sidebarFocusWithin || sidebarPointerDown);

  const isBleed = contentVariant === 'bleed';
  const shouldShowSubtitle = showSubtitle ?? Boolean(subtitle);
  const effectiveShowHeaderTitle = showHeaderTitle ?? showHeader;
  const shouldRenderHeader = effectiveShowHeaderTitle || showHeaderActions || (showTabs && tabsPlacement === 'before-title');
  const headerHasRail = showTabs && tabsPlacement === 'before-title';
  const assistantAvailable = showAssistant;
  const effectiveAssistantExpanded = assistantAvailable
    ? (lockAssistantOpen ? true : (assistantPanelV2Parity.active ? true : assistantExpanded))
    : false;
  const effectiveAssistantWidthMode = assistantPanelV2Parity.widthMode ?? (
    assistantWidthMode === 'expanded'
    || assistantWidthMode === 'half'
    || assistantWidthMode === 'full'
  )
    ? (assistantPanelV2Parity.widthMode ?? assistantWidthMode)
    : 'compact';
  const assistantVisible = showAssistant && effectiveAssistantExpanded;
  const personaLabel = 'Staff';
  const handleLogout = useCallback(async () => {
    await runBlockingLogout({
      action: onLogout,
      state: logoutStateRef.current,
      onPendingChange: setLogoutPending,
    });
  }, [onLogout]);

  const navigateToRoute = useCallback((route: string) => {
    if (typeof window === 'undefined') return;
    window.location.assign(route);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    let attempts = 0;
    const refreshProfile = () => {
      attempts += 1;
      const nextProfile = resolveCognitoProfile();
      setUserProfile((currentProfile) => (
        areCognitoProfilesEquivalent(currentProfile, nextProfile)
          ? currentProfile
          : nextProfile
      ));
      if ((nextProfile?.email && nextProfile?.name) || attempts >= 8) {
        window.clearInterval(intervalId);
      }
    };

    const intervalId = window.setInterval(refreshProfile, 1500);
    refreshProfile();

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (event.key.endsWith('.idToken') || event.key.includes('triedge_persona')) {
        refreshProfile();
      }
    };
    const handleSessionUpdated = () => {
      refreshProfile();
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('ystnj-auth-session-updated', handleSessionUpdated);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('ystnj-auth-session-updated', handleSessionUpdated);
    };
  }, []);

  const userDisplayName = userProfile?.name || userProfile?.email || 'Signed In User';
  const userDisplayEmail = userProfile?.email || '';
  const showNightModeToggle = canUseShellThemeToggle(userProfile);
  const userRoleLabel = resolveAppShellPersonaLabel(
    userProfile?.persona ?? 'guest',
    personaLabel,
    { isSuperAdmin: userProfile?.isSuperAdmin === true },
  );
  const userAvatarLabel = getInitials(userDisplayName);
  const notifications = useNotificationsCenter({
    enabled: showHeaderActions,
    participantId: userProfile?.userSub,
    familyId: SCHOOL_PERSONA_NOTIFICATION_STREAM_KEY,
  });
  const hasActiveSecondaryNavItem = useMemo(
    () => secondaryNavItems.some((item) => item.id === activeNavId),
    [activeNavId, secondaryNavItems],
  );
  const tempNavDisclosure = useTempNavDisclosure({
    storageKey: STAFF_TEMP_NAV_DISCLOSURE_STORAGE_KEY,
    forceOpen: hasActiveSecondaryNavItem,
  });
  const mappedNotificationMenuItems = useMemo(
    () => mapCrossHeaderNotificationsToMenuItems(notifications.items),
    [notifications.items]
  );
  const hasNotificationOverrides = notificationMenuItems !== undefined
    || notificationUnreadCount !== undefined
    || notificationIsLoading !== undefined
    || notificationError !== undefined;
  const effectiveNotificationMenuItems = notificationMenuItems ?? mappedNotificationMenuItems;
  const effectiveNotificationUnreadCount = notificationUnreadCount ?? notifications.unreadCount;
  const effectiveNotificationIsLoading = notificationIsLoading ?? notifications.isLoading;
  const effectiveNotificationError = notificationError ?? notifications.error;
  const handleNotificationSelect = useCallback((item: NotificationMenuItem) => {
    if (notificationSelectionPendingRef.current) return;
    notificationSelectionPendingRef.current = true;
    setNotificationsOpen(false);

    void runSafeNotificationNavigation(async () => {
      if (!hasNotificationOverrides) {
        try {
          await notifications.markNotificationRead(item.id);
        } catch (error) {
          console.warn('[AppShell] Failed to mark notification read before navigation', error);
        }
      }
      const route = resolveStaffNotificationRoute(item);
      if (onNotificationSelect) {
        await onNotificationSelect(item.id, route);
      } else if (route) {
        navigateToRoute(route);
      }
    }, '[AppShell] Notification navigation failed').finally(() => {
      notificationSelectionPendingRef.current = false;
    });
  }, [hasNotificationOverrides, navigateToRoute, notifications, onNotificationSelect]);

  const handleNavToggle = () => {
    const next = !navPinnedOpen;
    setNavPinnedOpen(next);
    onNavOpenChange?.(next);
  };

  const closeMobileNavIfNeeded = () => {
    if (!isMobileViewport || !navPinnedOpen) return;
    setNavPinnedOpen(false);
    onNavOpenChange?.(false);
  };

  const handleNavItemSelect = (id: string) => {
    onNavChange?.(id);
    closeMobileNavIfNeeded();
  };

  const handleCategoryChange = (nextId: string) => {
    const normalized = normalizeCategory(nextId);
    onCategoryChange?.(normalized);
  };

  const handleAssistantToggle = () => {
    if (lockAssistantOpen) {
      setAssistantExpanded(true);
      onAssistantOpenChange?.(true);
      return;
    }
    const next = !assistantExpanded;
    setAssistantExpanded(next);
    onAssistantOpenChange?.(next);
  };

  const handleAssistantClose = () => {
    if (lockAssistantOpen) {
      setAssistantExpanded(true);
      onAssistantOpenChange?.(true);
      return;
    }
    setAssistantExpanded(false);
    onAssistantOpenChange?.(false);
  };

  const handleAssistantWidthToggle = () => {
    if (!assistantResizable) {
      return;
    }
    const nextMode = effectiveAssistantWidthMode === 'compact'
      ? 'expanded'
      : effectiveAssistantWidthMode === 'expanded'
        ? 'half'
        : 'compact';
    onAssistantWidthModeChange?.(nextMode);
  };

  const defaultAssistant = assistantAvailable ? (
    <div
      id="admissions-ai-overlay"
      data-ai-layout="sidebar"
      data-testid="app-shell-ai-overlay-mount"
    />
  ) : null;

  useEffect(() => {
    if (!assistantAvailable || typeof window === 'undefined') {
      return () => {};
    }
    const handleOverlayCloseRequest = () => {
      if (lockAssistantOpen) {
        setAssistantExpanded(true);
        onAssistantOpenChange?.(true);
        return;
      }
      if (!assistantExpanded) {
        return;
      }
      setAssistantExpanded(false);
      onAssistantOpenChange?.(false);
    };

    window.addEventListener(ASSISTANT_OVERLAY_REQUEST_CLOSE_EVENT, handleOverlayCloseRequest as EventListener);
    return () => {
      window.removeEventListener(ASSISTANT_OVERLAY_REQUEST_CLOSE_EVENT, handleOverlayCloseRequest as EventListener);
    };
  }, [assistantAvailable, assistantExpanded, lockAssistantOpen, onAssistantOpenChange]);

  useEffect(() => {
    if (!assistantAvailable) {
      return;
    }
    emitAssistantShellState({
      expanded: effectiveAssistantExpanded,
      source: 'app-shell',
    });
  }, [assistantAvailable, effectiveAssistantExpanded]);

  useEffect(() => {
    if (!notificationsOpen) {
      return undefined;
    }
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const panel = document.querySelector('[data-testid="app-shell-notifications-panel"]');
      if (panel?.contains(targetNode)) {
        return;
      }
      if (!headerButtonsRef.current?.contains(targetNode)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [notificationsOpen]);

  const renderTabs = () => (
    <div
      className={`${styles.tabs} ${tabsOrientation === 'vertical' ? styles.tabsVertical : styles.tabsHorizontal}`}
      data-testid="app-shell-tabs"
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={`${styles.tabButton} ${activeTab?.id === tab.id ? styles.tabButtonActive : ''}`}
          onClick={() => handleCategoryChange(tab.id)}
        >
          <tab.icon className={styles.iconSm} />
          {tab.label}
        </button>
      ))}
    </div>
  );

  const resolvedAssistant = assistant === undefined ? defaultAssistant : assistant;

  return (
    <div
      className={`${styles.shell} ${forceSidebarRailOnCompact ? styles.shellForceSidebarRailOnCompact : ''}`}
      data-shared-shell-theme={nightModeEnabled ? 'dark' : 'light'}
      data-shell-render-policy="app-filter"
      data-extracted-from="owned:ystnj/shell/staff-shell-root"
      data-assistant-default={assistantDefaultMode}
      data-force-sidebar-rail-on-compact={forceSidebarRailOnCompact ? 'true' : 'false'}
    >
      <aside
        className={`${styles.sidebar} ${navExpanded ? styles.sidebarOpen : styles.sidebarCollapsed}`}
        aria-label="Primary navigation"
        data-testid="app-shell-sidebar"
        data-test-id="app-shell-sidebar"
        data-expanded={navExpanded ? 'true' : 'false'}
        data-extracted-from="owned:ystnj/shell/staff-sidebar"
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        onFocusCapture={() => setSidebarFocusWithin(true)}
        onBlurCapture={(event) => {
          const nextTarget = event.relatedTarget as Node | null;
          if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
            setSidebarFocusWithin(false);
          }
        }}
        onPointerDownCapture={() => setSidebarPointerDown(true)}
      >
        <div className={styles.sidebarHeader}>
          <div className={styles.brandIcon} data-testid="app-shell-brand-icon" aria-label="Intellio">
            I
          </div>
          <span className={styles.brandLabel}>Intellio</span>
          <button className={styles.sidebarToggle} type="button" onClick={handleNavToggle}>
            {navExpanded ? (
              <IconChevronLeft className={styles.iconSm} />
            ) : (
              <IconChevronRight className={styles.iconSm} />
            )}
          </button>
        </div>
        <div className={styles.sidebarBody}>
          <nav
            className={styles.navList}
            data-testid="app-shell-nav-list"
            data-extracted-from="owned:ystnj/shell/staff-nav-list"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.navItem} ${activeNavId === item.id ? styles.navItemActive : ''}`}
                onClick={() => handleNavItemSelect(item.id)}
                data-testid={`app-shell-nav-item-${item.id}`}
              >
                <span className={styles.navIcon} data-testid={`app-shell-nav-icon-${item.id}`}>
                  <item.icon className={styles.iconMd} />
                </span>
                <span className={styles.navLabel}>{item.label}</span>
              </button>
            ))}
          </nav>
          {secondaryNavItems.length ? (
            <section
              className={`${styles.tempSection} ${!tempNavDisclosure.expanded ? styles.tempSectionCollapsed : ''}`}
              aria-label={secondaryNavLabel}
              data-testid="app-shell-secondary-section"
              data-extracted-from="owned:ystnj/shell/staff-temp-nav-footer"
            >
              <button
                type="button"
                className={styles.sectionToggle}
                onClick={tempNavDisclosure.toggle}
                aria-expanded={tempNavDisclosure.expanded}
                aria-controls="app-shell-secondary-panel"
                aria-disabled={tempNavDisclosure.lockedOpen ? 'true' : undefined}
                data-testid="app-shell-secondary-toggle"
              >
                <span className={styles.sectionToggleLabel}>{secondaryNavLabel}</span>
                <IconChevronRight
                  className={`${styles.sectionToggleIcon} ${tempNavDisclosure.expanded ? styles.sectionToggleIconExpanded : ''}`}
                />
              </button>
              {tempNavDisclosure.expanded ? (
                <div
                  id="app-shell-secondary-panel"
                  className={styles.secondaryNavPanel}
                  data-testid="app-shell-secondary-panel"
                >
                  <div className={styles.secondaryNavList} data-testid="app-shell-secondary-nav-list">
                    {secondaryNavItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`${styles.navItem} ${activeNavId === item.id ? styles.navItemActive : ''}`}
                        onClick={() => handleNavItemSelect(item.id)}
                        data-testid={`app-shell-nav-item-${item.id}`}
                      >
                        <span className={styles.navIcon} data-testid={`app-shell-nav-icon-${item.id}`}>
                          <item.icon className={styles.iconMd} />
                        </span>
                        <span className={styles.navLabel}>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
        <div className={styles.sidebarBottom}>
          <div className={styles.navFooter} data-extracted-from="owned:ystnj/shell/staff-nav-footer">
            <div className={styles.navSectionLabel} data-testid="app-shell-system-label">
              System
            </div>
            {settingsItem ? (
              <button
                type="button"
                className={`${styles.navItem} ${activeNavId === settingsItem.id ? styles.navItemActive : ''}`}
                onClick={() => handleNavItemSelect(settingsItem.id)}
                data-testid="app-shell-settings-item"
              >
                <span className={styles.navIcon} data-testid="app-shell-settings-icon">
                  <settingsItem.icon className={styles.iconMd} />
                </span>
                <span className={styles.navLabel}>{settingsItem.label}</span>
              </button>
            ) : null}
            <button
              className={`${styles.navItem} g_id_signout`}
              type="button"
              onClick={handleLogout}
              disabled={logoutPending}
              aria-busy={logoutPending ? 'true' : undefined}
              data-testid="app-shell-logout-item"
            >
              <span className={styles.navIcon}>
                <IconLogout className={styles.iconMd} />
              </span>
              <span className={styles.navLabel}>Log out</span>
            </button>
          </div>
          <div className={styles.userCard} data-extracted-from="owned:ystnj/shell/staff-user-card">
            <div className={styles.userMeta}>
              <div className={styles.userAvatar} data-testid="app-shell-user-avatar">
                {userAvatarLabel}
              </div>
              {userCardAction ? (
                <div className={styles.userCardAction} data-testid="app-shell-user-card-action">
                  {userCardAction}
                </div>
              ) : null}
              <div className={styles.userDetails}>
                <div className={styles.userName} data-testid="app-shell-user-name">
                  {userDisplayName}
                </div>
                <div className={styles.userRole} data-testid="app-shell-user-role">
                  {userRoleLabel}
                </div>
                {userDisplayEmail ? (
                  <div className={styles.userEmail} data-testid="app-shell-user-email">
                    {userDisplayEmail}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className={styles.footerBrand} data-testid="app-shell-footer-brand" aria-hidden="true">
            <div className={styles.footerBrandName}>Intellio.</div>
            <div className={styles.footerBrandCopy}>© 2026 Intellio Labs, Inc.</div>
          </div>
        </div>
      </aside>
      {isMobileViewport && navExpanded ? (
        <button
          type="button"
          className={styles.mobileNavBackdrop}
          aria-label="Close menu"
          onClick={handleNavToggle}
        />
      ) : null}

      <main
        className={`${styles.main} ${assistantVisible ? styles.mainWithAssistant : ''} ${
          assistantVisible && effectiveAssistantWidthMode === 'expanded'
            ? styles.mainWithAssistantExpanded
            : assistantVisible && effectiveAssistantWidthMode === 'half'
              ? styles.mainWithAssistantHalf
              : assistantVisible && effectiveAssistantWidthMode === 'full'
                ? styles.mainWithAssistantFull
                : ''
        } ${
          isBleed ? styles.mainBleed : styles.mainPanel
        } ${mainPaddingProfileClass} ${mainLayoutClass}`}
        data-extracted-from="owned:ystnj/shell/staff-main"
        style={forceMainMarginRightZero ? { marginRight: 0 } : undefined}
      >
        {shouldRenderHeader ? (
          <header
            className={`${styles.header} ${headerHasRail ? styles.headerWithRail : ''} ${!effectiveShowHeaderTitle && !headerHasRail ? styles.headerActionsOnly : ''} ${headerClassName}`.trim()}
            data-testid="app-shell-header"
            data-extracted-from="owned:ystnj/shell/staff-header"
            style={{ marginBottom: 0 }}
          >
            {headerHasRail ? (
              <div className={styles.headerRail}>{renderTabs()}</div>
            ) : null}
            {effectiveShowHeaderTitle ? (
              <div className={styles.headerMain} style={{ marginBottom: 0 }}>
                <button
                  type="button"
                  className={styles.mobileMenuToggle}
                  data-testid="app-shell-menu-toggle"
                  aria-label="Toggle Sidebar"
                  aria-expanded={navExpanded ? 'true' : 'false'}
                  onClick={handleNavToggle}
                >
                  {navExpanded ? (
                    <IconLayoutSidebarLeftCollapse
                      className={`${styles.mobileMenuIcon} lucide lucide-indent-decrease`}
                      data-testid="app-shell-menu-toggle-icon-expanded"
                    />
                  ) : (
                    <IconLayoutSidebarLeftExpand
                      className={`${styles.mobileMenuIcon} lucide lucide-indent-increase`}
                      data-testid="app-shell-menu-toggle-icon-collapsed"
                    />
                  )}
                </button>
                <h1 className={styles.headerTitle}>{title}</h1>
                {shouldShowSubtitle ? <div className={styles.headerSubtitle}>{subtitle}</div> : null}
              </div>
            ) : null}
            {showHeaderActions ? (
              <div className={styles.headerActions}>
                {status ? <div className={styles.status}>{status}</div> : null}
                <div ref={headerButtonsRef} className={styles.headerButtons}>
                  {headerAdminChrome}
                  <CrossHeaderActions
                    testIdPrefix="app-shell"
                    assistantExpanded={effectiveAssistantExpanded}
                    notificationsOpen={notificationsOpen}
                    unreadCount={effectiveNotificationUnreadCount}
                    onAssistantToggle={handleAssistantToggle}
                    onNotificationsToggle={() => setNotificationsOpen((open) => !open)}
                    assistantWidthMode={effectiveAssistantWidthMode}
                    onAssistantWidthModeChange={onAssistantWidthModeChange}
                    onAssistantClose={handleAssistantClose}
                    middleSlot={showNightModeToggle
                      ? (
                          <ShellThemeToggle
                            enabled={nightModeEnabled}
                            onToggle={toggleNightMode}
                            testId="app-shell-header-night-mode-toggle"
                          />
                        )
                      : undefined}
                  />
                    <CrossHeaderNotificationsMenu
                      testIdPrefix="app-shell"
                      open={notificationsOpen}
                      unreadCount={effectiveNotificationUnreadCount}
                      items={effectiveNotificationMenuItems}
                      isLoading={effectiveNotificationIsLoading}
                      error={effectiveNotificationError}
                      onClose={() => setNotificationsOpen(false)}
                      onSelectItem={handleNotificationSelect}
                    />
                </div>
              </div>
            ) : null}
          </header>
        ) : null}

        {showTabs && tabsPlacement === 'below-header' ? renderTabs() : null}

        {adminChromeBanner}
        <section
          className={`${styles.content} ${contentInsetClass}`}
          data-testid="app-shell-content-root"
          data-extracted-from="owned:ystnj/shell/staff-content"
          data-shell-content-min-inset={contentInsetMode === 'none' ? '0' : '16'}
        >
          {children}
        </section>
      </main>

      {showAssistant ? (
        <aside
          className={`${styles.assistant} ${effectiveAssistantExpanded ? styles.assistantOpen : styles.assistantClosed} ${
            effectiveAssistantWidthMode === 'expanded'
              ? styles.assistantExpanded
              : effectiveAssistantWidthMode === 'half'
                ? styles.assistantHalf
                : effectiveAssistantWidthMode === 'full'
                  ? styles.assistantFull
                  : ''
          }`}
          aria-label="Intellio assistant"
          aria-hidden={effectiveAssistantExpanded ? undefined : true}
          data-testid="app-shell-assistant"
          data-expanded={effectiveAssistantExpanded ? 'true' : 'false'}
          data-assistant-width-mode={effectiveAssistantWidthMode}
          data-extracted-from="owned:ystnj/shell/staff-assistant"
        >
          {assistantResizable && effectiveAssistantExpanded ? (
            <button
              type="button"
              className={styles.assistantWidthToggle}
              onClick={handleAssistantWidthToggle}
              aria-label={effectiveAssistantWidthMode === 'half' ? 'Restore assistant panel width' : 'Widen assistant panel'}
              title={effectiveAssistantWidthMode === 'half' ? 'Restore assistant panel width' : 'Widen assistant panel'}
              data-testid="app-shell-assistant-width-toggle"
            >
              {effectiveAssistantWidthMode === 'half'
                ? (
                  <IconLayoutSidebarRightCollapse
                    className={styles.iconMd}
                    data-testid="app-shell-assistant-width-toggle-icon-restore"
                  />
                )
                : (
                  <IconLayoutSidebarRightExpand
                    className={styles.iconMd}
                    data-testid="app-shell-assistant-width-toggle-icon-widen"
                  />
                )}
            </button>
          ) : null}
          <div className={styles.assistantBody}>
            {resolvedAssistant ? (
              <div
                className={styles.assistantMount}
                data-testid="intellio-assistant"
                data-extracted-from="owned:ystnj/shell/staff-assistant-mount"
              >
                {resolvedAssistant}
              </div>
            ) : null}
          </div>
        </aside>
      ) : null}
    </div>
  );
};
